import * as THREE from 'three';

import React, { useRef } from 'react';

import { useFrame } from '@react-three/fiber';

interface RasenganProps {
  position: [number, number, number];
  playerId: string;
}

export const Rasengan: React.FC<RasenganProps> = ({ position }) => {
  const rasenganRef = useRef<THREE.Group>(null);
  const particlesRef = useRef<THREE.Points>(null);

  // Create spinning animation
  useFrame((state) => {
    if (rasenganRef.current) {
      rasenganRef.current.rotation.y += 0.05;
      rasenganRef.current.rotation.z += 0.03;
    }

    // Animate particles
    if (particlesRef.current) {
      const particles = particlesRef.current;
      const positions = particles.geometry.attributes.position;
      const time = state.clock.getElapsedTime();

      for (let i = 0; i < positions.count; i++) {
        const x = positions.getX(i);
        const y = positions.getY(i);
        const z = positions.getZ(i);

        // Create swirling motion
        const angle = time * 2 + i * 0.01;
        const radius = 0.1 * Math.sin(time * 0.5 + i * 0.1);

        positions.setX(i, x + Math.cos(angle) * radius);
        positions.setY(i, y + Math.sin(angle) * radius);
        positions.setZ(i, z + Math.sin(time + i) * 0.02);
      }

      positions.needsUpdate = true;
    }
  });

  // Create particles for the swirling effect
  const particleCount = 200;
  const particlePositions = new Float32Array(particleCount * 3);

  for (let i = 0; i < particleCount; i++) {
    const radius = 0.7 * Math.random();
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.random() * Math.PI;

    const x = radius * Math.sin(phi) * Math.cos(theta);
    const y = radius * Math.sin(phi) * Math.sin(theta);
    const z = radius * Math.cos(phi);

    particlePositions[i * 3] = x;
    particlePositions[i * 3 + 1] = y;
    particlePositions[i * 3 + 2] = z;
  }

  return (
    <group ref={rasenganRef} position={position}>
      {/* Main Rasengan sphere */}
      <mesh castShadow>
        <sphereGeometry args={[0.5, 16, 16]} />
        <meshStandardMaterial
          color="#4287f5"
          emissive="#2a5cac"
          emissiveIntensity={2}
          transparent={true}
          opacity={0.7}
        />
      </mesh>

      {/* Inner core */}
      <mesh>
        <sphereGeometry args={[0.3, 16, 16]} />
        <meshStandardMaterial
          color="#ffffff"
          emissive="#ffffff"
          emissiveIntensity={1}
        />
      </mesh>

      {/* Swirling particles */}
      <points ref={particlesRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={particleCount}
            array={particlePositions}
            itemSize={3}
          />
        </bufferGeometry>
        <pointsMaterial
          size={0.05}
          color="#86b5ff"
          transparent={true}
          opacity={0.8}
        />
      </points>

      {/* Light source */}
      <pointLight intensity={2} distance={3} color="#4287f5" />
    </group>
  );
};
